import { Component, Inject, OnInit, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { RemoveNoteUsecase } from "@arch/domain/application/usecase/remove-note.usecase";
import {
    NoteEntity,
    ReportEntity,
    VisualizationEntity
} from "@arch/domain/entity";
import { EventsService } from "@arch/domain/application/services/events.service";
import { ContactService } from "@app/shared/services/contact.service";
import {
    GetNoteExplanationController,
    SaveNoteController
} from "@arch/presentation/controllers";
import { AccountingPlanService } from "@app/shared/services/accounting-plan.service";

@Component({
    selector: "app-add-notes-dialog",
    imports: [SharedMaterialModule, SharedDsnModule],
    templateUrl: "./add-notes-dialog.component.html",
    styleUrl: "./add-notes-dialog.component.scss"
})
export class AddNotesDialogComponent implements OnInit {
    readonly removeNoteController = inject(RemoveNoteUsecase);
    private readonly saveNoteController = inject(SaveNoteController);
    private readonly getNoteExplanationController = inject(
        GetNoteExplanationController
    );
    private readonly contactService = inject(ContactService);
    public loading: boolean = false;
    public generatingNote: boolean = false;
    public comment: string = "";
    private readonly eventsService = inject(EventsService);
    public readonly accountingPlanService = inject(AccountingPlanService);
    constructor(
        public dialogRef: MatDialogRef<AddNotesDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: AddNotesDialogDataInputType
    ) {}

    ngOnInit(): void {
        this.comment = this.data.visualization.note?.props.comment || "";
    }

    public handleInput(comment: string): void {
        this.comment = comment;
    }

    // Método para converter HTML para texto limpo para armazenamento no backend
    private convertHtmlToText(html: string): string {
        if (!html) return "";
        // Criar um elemento temporário para extrair o texto
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        return tempDiv.textContent || tempDiv.innerText || "";
    }

    // Método para verificar se o conteúdo não está vazio (considerando HTML)
    public isContentEmpty(html: string): boolean {
        if (!html) return true;
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        const textContent = tempDiv.textContent || tempDiv.innerText || "";
        return textContent.trim().length === 0;
    }
    public async handleSubmit(): Promise<void> {
        this.eventsService.send(
            `Dash.${this.data.report.type}.${this.data.report.humanSourceName}.criar-nota`
        );
        const user = this.contactService.get();
        this.loading = true;
        if (!this.data.visualization.note) {
            this.data.visualization.note = NoteEntity.restore({
                createdAt: new Date(),
                updatedAt: new Date()
            });
        }
        // Armazenar o HTML formatado no campo comment
        this.data.visualization.note.setComment(this.comment);

        const note = await this.saveNoteController.execute({
            reportId: this.data.report.props._id,
            ...(this.data.visualization.note.props._id && {
                _id: this.data.visualization.note.props._id
            }),
            comment: this.data.visualization.note.props.comment,
            accountingId: this.data.report.props.accountingId,
            companyId: this.data.report.props.companyId,
            report: {
                name: this.data.report.props.name,
                _id: this.data.report.props._id
            },
            period: this.data.report.props.period,
            date: {
                year: this.data.report.options.endDate.getUTCFullYear(),
                month: this.data.report.options.endDate.getUTCMonth() + 1
            },
            scenarioId: this.data.report.props.scenario,
            visualizationId: this.data.visualization.props._id,
            userId: user?.props._id,
            serieId: this.data.visualization.props.series[0]
        });

        this.data.visualization.note.setUserId(user.props._id);
        this.data.visualization.note.setCreatedAt(new Date());
        this.data.visualization.note.noteId = note._id;

        this.dialogRef.close(this.data.visualization.note);

        this.loading = false;
    }

    async handleRemoveNote(): Promise<void> {
        this.loading = true;
        await this.removeNoteController.execute({
            _id: this.data.visualization.note.props._id
        });
        this.data.visualization.destroyNote();
        this.comment = "";
        this.loading = false;
    }

    async onGenerateNote(): Promise<void> {
        if (this.generatingNote) return;
        this.generatingNote = true;
        const response = await this.getNoteExplanationController.execute({
            companyId: this.data.report.props.companyId,
            options: {
                visualizationsIds: [this.data.visualization.props._id],
                period: this.data.report.options.period,
                scenarioId: this.data.report.options.scenario.props._id,
                dates: this.data.report.options.dates,
                accountingId: this.data.report.props.accountingId,
                options: {
                    isGroup: this.data.report.props.isGroup,
                    projection: {
                        totalMonths: 12,
                        scenarioId: this.data.report.options.projection
                    },
                    goalsIds: [],
                    budget: {
                        scenarioId: this.data.report.options.budget
                    }
                }
            }
        });
        this.comment = response?.message || "";
        this.generatingNote = false;
    }
}

type AddNotesDialogDataInputType = {
    visualization: VisualizationEntity;
    report: ReportEntity;
};
